import { spotifyQueries, SpotifyArtistSearchResult } from '@round/api';
import { ComboboxItem } from '@round/creatorbase-ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { UIEventHandler, useCallback, useMemo, useState } from 'react';

const queryOptions = spotifyQueries.artistSearch.options.infinite;

const LIST_SCROLL_THRESHOLD_PX = 80;

export type SpotifyArtistItem = ComboboxItem<string> & {
    spotify_url: string;
    image_url: string | null;
    genres: string[];
    followers?: number;
};

export const getSpotifyArtistUrl = (spotifyId: string): string => `https://open.spotify.com/artist/${spotifyId}`;

export const mapToItem = (artist: SpotifyArtistSearchResult): SpotifyArtistItem => ({
    value: artist.spotify_id,
    label: artist.name,
    spotify_url: artist.spotify_url,
    image_url: artist.image_url,
    genres: artist.genres,
    followers: artist.followers,
});

export function useSpotifyArtistCombobox() {
    const [search, setSearch] = useState('');
    const isSearching = search.length > 0;

    const { data, isFetching, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
        ...queryOptions({ keyword: search, page_size: 10 }),
        enabled: isSearching,
    });

    const items = useMemo(() => data?.pages.flatMap((page) => page.results.map(mapToItem)) ?? [], [data]);

    const onItemsListScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        (event) => {
            const el = event.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight > LIST_SCROLL_THRESHOLD_PX) {
                return;
            }
            if (hasNextPage && !isFetching) {
                void fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetching]
    );

    return {
        items,
        isFetching,
        isError,
        isSearching,
        /** Disables Base UI client-side filtering; list is driven by API keyword via debounced query. */
        filter: null,
        onInputValueChange: setSearch,
        onItemsListScroll,
    };
}
